/**
 * AI Search Snapshot — 5-engine visibility card.
 *
 * Visual: one tight card showing Google + ChatGPT + Perplexity + Gemini +
 * Bing AI scores side-by-side. Replicates Stape's borrowed-authority pattern
 * (PageSpeed Insights mockup) but with our own 5-engine differentiator that
 * no single-engine competitor can copy.
 *
 * Used in 2 places:
 *  1. Inline on results page (auto-fetches when `domain` prop is set)
 *  2. Standalone via /preview/:domain public viral page
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";

const ENGINES = [
  { key: "google",     label: "Google",     color: "#4285F4" },
  { key: "chatgpt",    label: "ChatGPT",    color: "#10A37F" },
  { key: "perplexity", label: "Perplexity", color: "#22B8CD" },
  { key: "gemini",     label: "Gemini",     color: "#A855F7" },
  { key: "bing_ai",    label: "Bing AI",    color: "#0078D4" },
];

export function AISnapshotCard({ domain, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!domain) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/ai-snapshot/${encodeURIComponent(domain)}`)
      .then(r => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [domain]);

  if (!domain) return null;
  if (loading) {
    return (
      <div className="w-full p-6 bg-white border border-gray-200 rounded-lg animate-pulse" data-testid="ai-snapshot-loading">
        <div className="h-4 w-32 bg-gray-200 rounded mb-4" />
        <div className="grid grid-cols-5 gap-3">
          {ENGINES.map(e => <div key={e.key} className="h-20 bg-gray-100 rounded" />)}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const e = data.engines || {};
  const avg = Math.round(ENGINES.reduce((a, en) => a + (e[en.key] || 0), 0) / ENGINES.length);

  return (
    <div className={`w-full bg-white border border-gray-200 rounded-lg ${compact ? "p-4" : "p-6"}`} data-testid="ai-snapshot-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-gray-400">AI Search Snapshot</p>
          <p className="text-sm font-bold text-gray-900 truncate" data-testid="ai-snapshot-domain">{data.domain}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-mono uppercase text-gray-400">Average</p>
          <p className="text-2xl font-black text-gray-900 leading-none">{avg}<span className="text-sm text-gray-400">/100</span></p>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-2 sm:gap-3">
        {ENGINES.map((en) => {
          const score = e[en.key] || 0;
          return (
            <div key={en.key} className="text-center" data-testid={`ai-snapshot-${en.key}`}>
              <div
                className="mx-auto rounded-full flex items-center justify-center text-white font-black"
                style={{
                  width: compact ? 50 : 64,
                  height: compact ? 50 : 64,
                  fontSize: compact ? 16 : 20,
                  background: `conic-gradient(${en.color} ${score * 3.6}deg, #f3f4f6 0deg)`,
                }}
              >
                <span
                  className="rounded-full bg-white text-gray-900 flex items-center justify-center"
                  style={{ width: compact ? 38 : 50, height: compact ? 38 : 50 }}
                >
                  {score}
                </span>
              </div>
              <p className="text-[10px] sm:text-xs font-medium text-gray-600 mt-1.5 truncate">{en.label}</p>
            </div>
          );
        })}
      </div>

      {!data.fresh && (
        <p className="text-[10px] text-gray-400 mt-3 text-center font-mono">
          Estimated — scan this site for real numbers
        </p>
      )}
    </div>
  );
}

export default AISnapshotCard;
